import React, { useContext } from 'react';
import { ButtonAccentPrimary, ButtonCta, ButtonPrimary, ButtonSecondary } from '@components/button';
import { LoaderSpinner } from '@components/loader-spinner';
import { FeatureFlagContext } from '@contexts/featureFlag/featureFlag-context';
import classNames from 'classnames';

import { ModalStickyFooterProps } from './modal-sticky-footer.type';

export const ModalStickyFooter = ({
  isAccent,
  isSubmit,
  isSubmitDisabled,
  isSubmitLoading,
  isSecondaryButtonDisabled,
  primaryButtonText,
  secondaryButtonText,
  tertiaryButtonText,
  primaryDataTestId,
  secondaryDataTestId,
  tertiaryDataTestId,
  tertiaryIcon,
  onPrimaryButtonClick,
  onSecondaryButtonClick,
  onTertiaryButtonClick,
}: ModalStickyFooterProps) => {
  const { isWebView } = useContext(FeatureFlagContext);
  const alignClasses = {
    'justify-between': onTertiaryButtonClick,
    'items-center justify-end': onSecondaryButtonClick && (onPrimaryButtonClick || isSubmit) && !onTertiaryButtonClick,
    'justify-end': !onSecondaryButtonClick && (onPrimaryButtonClick || isSubmit) && !onTertiaryButtonClick,
  };

  const ButtonPrimaryComponent = isAccent ? ButtonAccentPrimary : ButtonPrimary;

  const groupedButtons = (
    <>
      {onSecondaryButtonClick && (
        <ButtonSecondary
          data-testid={secondaryDataTestId}
          onClick={onSecondaryButtonClick}
          disabled={isSecondaryButtonDisabled}>
          {secondaryButtonText}
        </ButtonSecondary>
      )}
      {isSubmit && (
        <ButtonPrimaryComponent
          data-testid={primaryDataTestId}
          type='submit'
          disabled={!!isSubmitDisabled || !!isSubmitLoading}>
          {isSubmitLoading ? <LoaderSpinner dataTestId='submit-loader' size={5} /> : primaryButtonText}
        </ButtonPrimaryComponent>
      )}
      {!isSubmit && onPrimaryButtonClick && (
        <ButtonPrimaryComponent
          data-testid={primaryDataTestId}
          onClick={onPrimaryButtonClick}
          disabled={!!isSubmitDisabled || !!isSubmitLoading}>
          {isSubmitLoading ? <LoaderSpinner dataTestId='submit-loader' size={5} /> : primaryButtonText}
        </ButtonPrimaryComponent>
      )}
    </>
  );

  const tertiaryButton = (
    <ButtonCta
      data-testid={tertiaryDataTestId}
      onClick={onTertiaryButtonClick}
      className={classNames('text-body-s md:text-body-m', { 'text-accent': isAccent, 'text-primary': !isAccent })}
      paddingClasses='p-0'>
      {tertiaryIcon ? (
        <div className='flex gap-2 items-center'>
          {tertiaryIcon} {tertiaryButtonText}
        </div>
      ) : (
        tertiaryButtonText
      )}
    </ButtonCta>
  );

  return (
    <footer
      className={classNames(
        'sticky bottom-0 flex flex-nowrap gap-3 sm:gap-6 w-full bg-primary shadow-custom px-4 sm:px-7 ',
        {
          'py-5': isWebView,
          'pt-3 pb-2 xs:pb-3 sm:pt-5 sm:pb-5': !isWebView,
        },
        alignClasses
      )}>
      {onTertiaryButtonClick ? (
        <>
          {tertiaryButton}
          <div className='ml-auto flex gap-3 sm:gap-6'>{groupedButtons}</div>
        </>
      ) : (
        groupedButtons
      )}
    </footer>
  );
};
